import { DeploymentBrands, DeploymentEnvironments, FacebookConfig } from '@missionlabs/types'

/**
 * Returns a regex that matches the given environment and url
 * @param env - The environment to match i.e. dev, uat, prod
 * @returns A regex that matches the given environment and url
 */
const getEnvRegex = (env?: string) => {
    const url =
        '(meetings|guestapp|webapp|cl\\-app|pl\\-app|app|app\\-new)\\.(viazeta|circleloop|phonelineplus)\\.com'

    // If an environment is provided, we want to match the environment and the url
    if (env) {
        return new RegExp(`^${env}-${url}$`, 'i')
    }

    // If no environment is provided, we want to just match the url
    return new RegExp(`^${url}$`, 'i')
}

const getSubDomain = (host: string) => {
    // Split the hostname into parts
    const parts = host.split('.')
    //subdomain will have more than 2 parts
    return parts.length > 2 ? parts[0] : undefined
}

const checkForEnv = (host: string, env: string) => {
    //First check if we have {env}.domain.com
    const subdomain = getSubDomain(host)
    if (subdomain === env) return true
    return getEnvRegex(env).test(host)
}

/**
 * Determines which environment the app is running in, based on:
 * - The app domain, for web app
 * - The DEPLOY_ENV environment variable, for electron apps
 */
export function getDeploymentEnvironment(): DeploymentEnvironments | undefined {
    // web deployment environment
    const { host } = window.location
    if (host.startsWith('localhost')) return DeploymentEnvironments.dev

    // Check for dev, uat, beta, prod in the subdomain
    if (checkForEnv(host, 'dev')) return DeploymentEnvironments.dev
    if (checkForEnv(host, 'uat')) return DeploymentEnvironments.uat
    if (checkForEnv(host, 'beta')) return DeploymentEnvironments.prod
    if (checkForEnv(host, 'prod')) return DeploymentEnvironments.prod

    // If no environment is found in the subdomain, default to prod
    if (getEnvRegex().test(host)) return DeploymentEnvironments.prod

    // For electron apps, we use the DEPLOY_ENV environment variable
    // since there is no domain for us to check
    const env = process.env.DEPLOY_ENV as DeploymentEnvironments | undefined
    if (env) return env
}

export const isBeta = (version: string) => {
    const hostIsBeta = getSubDomain(window.location.host) === 'beta'
    return hostIsBeta || version.includes('-beta')
}

type EnvironmentValueMap = Partial<Record<DeploymentEnvironments, string>>

const parseEnvironmentMap = (raw: string | undefined): EnvironmentValueMap | undefined => {
    if (!raw) return undefined

    try {
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') return undefined

        return parsed as EnvironmentValueMap
    } catch {
        return undefined
    }
}

const resolveEnvironment = (environment?: DeploymentEnvironments): DeploymentEnvironments => {
    return environment ?? getDeploymentEnvironment() ?? DeploymentEnvironments.dev
}

const resolveEnvironmentValue = (
    staticValue: string | undefined,
    map: EnvironmentValueMap | undefined,
    environment: DeploymentEnvironments
) => map?.[environment] ?? staticValue ?? ''

export const getFlagsmithEnvironment = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)
    const map = parseEnvironmentMap(process.env.FLAGSMITH_ENVIRONMENT_IDS)

    return resolveEnvironmentValue(process.env.FLAGSMITH_ENVIRONMENT_ID, map, selectedEnvironment)
}

export const getConfigCatSdkKey = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)
    const map = parseEnvironmentMap(process.env.CONFIGCAT_SDK_KEYS)

    return resolveEnvironmentValue(process.env.CONFIGCAT_SDK_KEY, map, selectedEnvironment)
}

export const getKeycloakEnvironment = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)

    return {
        url: resolveEnvironmentValue(
            process.env.KEYCLOAK_URL,
            parseEnvironmentMap(process.env.KEYCLOAK_URLS),
            selectedEnvironment
        ),
        realm: resolveEnvironmentValue(
            process.env.KEYCLOAK_REALM,
            parseEnvironmentMap(process.env.KEYCLOAK_REALMS),
            selectedEnvironment
        ),
        clientId: resolveEnvironmentValue(
            process.env.KEYCLOAK_CLIENT_ID,
            parseEnvironmentMap(process.env.KEYCLOAK_CLIENT_IDS),
            selectedEnvironment
        )
    }
}

export const getDownloadBucketEnvironment = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)
    const map = parseEnvironmentMap(process.env.DESKTOP_DOWNLOAD_BUCKETS)

    return resolveEnvironmentValue(process.env.DESKTOP_DOWNLOAD_BUCKET, map, selectedEnvironment)
}

export const getWebrtcStatsKey = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)
    const map = parseEnvironmentMap(process.env.WEBRTC_STATS_KEYS)

    return resolveEnvironmentValue(process.env.WEBRTC_STATS_KEY, map, selectedEnvironment)
}

export const getFacebookEnvironment = (environment?: DeploymentEnvironments): FacebookConfig => {
    const selectedEnvironment = resolveEnvironment(environment)
    const appIDMap = parseEnvironmentMap(process.env.FB_APP_IDS)
    const configIDMap = parseEnvironmentMap(process.env.FB_CONFIG_IDS)

    return {
        appID: resolveEnvironmentValue(process.env.FB_APP_ID, appIDMap, selectedEnvironment),
        configID: resolveEnvironmentValue(
            process.env.FB_CONFIG_ID,
            configIDMap,
            selectedEnvironment
        )
    }
}

export const getLogsEnvironment = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)
    const map = parseEnvironmentMap(process.env.APPLICATION_LOG_API_KEYS)

    return resolveEnvironmentValue(process.env.APPLICATION_LOG_API_KEY, map, selectedEnvironment)
}

export const getStripeEnvironment = (environment?: DeploymentEnvironments) => {
    const selectedEnvironment = resolveEnvironment(environment)
    const map = parseEnvironmentMap(process.env.STRIPE_API_KEYS)

    return resolveEnvironmentValue(process.env.STRIPE_API_KEY, map, selectedEnvironment)
}

interface PopulateUrlParams {
    dataCentre: string
    environment: string
    country: string
    product?: string
}

export function populateUrl(
    url: string,
    params: PopulateUrlParams,
    replacePreceding?: boolean
): string {
    const { dataCentre, environment, country, product } = params
    if (dataCentre === 'localhost') return 'http://localhost:8000/zeta'

    let sanitizedUrl = url

    const brand = product ? DeploymentBrands[product] : ''

    // We want -dev and -uat, but for prod we just want to remove the environment altogether
    if (environment === DeploymentEnvironments.prod) {
        sanitizedUrl = sanitizedUrl.replace('-{environment}', '')
        if (replacePreceding) sanitizedUrl = sanitizedUrl.replace('{environment}-', '')
    }

    return sanitizedUrl
        .replace('{datacentre}', dataCentre)
        .replace('{environment}', environment)
        .replace('{country}', country)
        .replace('{product}', product ?? '')
        .replace('{brand}', brand ?? '')
}
